import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { getProductImageUrl } from '../../../utils/productImage';
import { hasDisplayPrice } from '../../../utils/priceDisplay';

const QuoteModal = ({ isOpen, onClose, product, quantity }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    position: '',
    urgency: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const urgencyOptions = [
    { value: '', label: 'Select urgency level' },
    { value: 'immediate', label: 'Immediate (Within 24 hours)' },
    { value: 'urgent', label: 'Urgent (Within 1 week)' },
    { value: 'normal', label: 'Normal (Within 2 weeks)' },
    { value: 'planning', label: 'Planning (Within 1 month)' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Show success message and close modal
    alert('Quote request submitted successfully! We will contact you within 24 hours.');
    setIsSubmitting(false);
    onClose();
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      organization: '',
      position: '',
      urgency: '',
      message: ''
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-background rounded-lg shadow-medical border border-border"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Request Quote</h2>
              <p className="text-sm text-text-secondary mt-1">
                Get a personalized quote for {product?.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors duration-200"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Product Summary */}
          <div className="p-6 bg-muted border-b border-border">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-lg border border-border overflow-hidden">
                <img
                  src={getProductImageUrl(product?.mainImage ?? product?.main_image) ?? undefined}
                  alt={product?.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-text-primary">{product?.name}</h3>
                <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary mt-1">
                  <span>SKU: {product?.sku ?? '—'}</span>
                  <span>Quantity: {quantity}</span>
                  {hasDisplayPrice(product?.price) ? (
                    <span className="font-medium text-primary">
                      {new Intl.NumberFormat('en-MY', {
                        style: 'currency',
                        currency: 'MYR',
                        minimumFractionDigits: 0
                      }).format(Number(product.price) * quantity)}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                type="text"
                required
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                placeholder="Enter your full name"
              />
              <Input
                label="Email Address"
                type="email"
                required
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                placeholder="your.email@hospital.com"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Phone Number"
                type="tel"
                required
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                placeholder="+60 12-345 6789"
              />
              <Input
                label="Organization"
                type="text"
                required
                value={formData?.organization}
                onChange={(e) => handleInputChange('organization', e?.target?.value)}
                placeholder="Hospital/Clinic name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Position/Title"
                type="text"
                value={formData?.position}
                onChange={(e) => handleInputChange('position', e?.target?.value)}
                placeholder="e.g., Chief Surgeon, Procurement Manager"
              />
              <Select
                label="Urgency Level"
                options={urgencyOptions}
                value={formData?.urgency}
                onChange={(value) => handleInputChange('urgency', value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Additional Requirements
              </label>
              <textarea
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                placeholder="Please specify any additional requirements, training needs, or questions..."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              />
            </div>

            {/* Terms */}
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <div className="text-sm text-text-secondary">
                  <p className="mb-2">
                    By submitting this form, you agree to our terms and conditions. 
                    Our team will contact you within 24 hours with a detailed quote.
                  </p>
                  <p>
                    For immediate assistance, call us at <strong>+60 3-2110 0348</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="left"
                className="flex-1"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default QuoteModal;